// Комментарий на русском: единая точка хранения авторизации.
// "Запомнить меня" => localStorage, иначе sessionStorage (авторизация живет до закрытия браузера).

const ACCESS_KEY = 'access';
const REFRESH_KEY = 'refresh';
const USER_KEY = 'user';

function notifyAuthChanged() {
  // Комментарий на русском: общий сигнал для UI (шапка/меню), что данные авторизации/профиля изменились.
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('lisad_auth_changed'));
  } catch {
    // Игнорируем: например, если Event недоступен в окружении
  }
}

function safeGet(storage: Storage, key: string) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: Storage, key: string, value: string) {
  try {
    storage.setItem(key, value);
  } catch {
    // Игнорируем: например, приватный режим/запрет хранения
  }
}

function safeRemove(storage: Storage, key: string) {
  try {
    storage.removeItem(key);
  } catch {
    // Игнорируем
  }
}

function decodeJwtExp(token: string): number | null {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const pad = normalized.length % 4;
    const b64 = normalized + (pad ? '='.repeat(4 - pad) : '');
    const raw = atob(b64);
    const payload = JSON.parse(raw) as { exp?: unknown };
    const exp = Number(payload?.exp);
    return Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

export function isJwtExpired(token: string): boolean {
  const exp = decodeJwtExp(token);
  if (!exp) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return exp <= nowSec + 10;
}

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return safeGet(localStorage, ACCESS_KEY) || safeGet(sessionStorage, ACCESS_KEY);
}

export function getValidAccessToken(): string | null {
  const token = getAccessToken();
  if (!token) return null;
  return isJwtExpired(token) ? null : token;
}

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return safeGet(localStorage, REFRESH_KEY) || safeGet(sessionStorage, REFRESH_KEY);
}

export function getStoredUserRaw(): string | null {
  if (typeof window === 'undefined') return null;
  return safeGet(localStorage, USER_KEY) || safeGet(sessionStorage, USER_KEY);
}

export function getAuthStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  const hasLocal = !!(safeGet(localStorage, ACCESS_KEY) || safeGet(localStorage, REFRESH_KEY) || safeGet(localStorage, USER_KEY));
  if (hasLocal) return localStorage;
  const hasSession = !!(safeGet(sessionStorage, ACCESS_KEY) || safeGet(sessionStorage, REFRESH_KEY) || safeGet(sessionStorage, USER_KEY));
  if (hasSession) return sessionStorage;
  return null;
}

export function setStoredUser(user: unknown) {
  if (typeof window === 'undefined') return;
  const storage = getAuthStorage() || localStorage;
  safeSet(storage, USER_KEY, JSON.stringify(user));
  notifyAuthChanged();
}

export function clearAuth() {
  if (typeof window === 'undefined') return;
  safeRemove(localStorage, ACCESS_KEY);
  safeRemove(localStorage, REFRESH_KEY);
  safeRemove(localStorage, USER_KEY);
  safeRemove(sessionStorage, ACCESS_KEY);
  safeRemove(sessionStorage, REFRESH_KEY);
  safeRemove(sessionStorage, USER_KEY);
  document.cookie = 'is_auth=; Path=/; Max-Age=0; SameSite=Lax';

  // Комментарий на русском: при логауте очищаем офлайн-данные чатов (персональные данные).
  try {
    if (typeof indexedDB !== 'undefined') indexedDB.deleteDatabase('lisad_chat_offline_v1');
  } catch {
    // ignore
  }

  // Комментарий на русском: при логауте очищаем постоянный кэш медиа (персональные фото/вложения).
  try {
    void import('@/utils/chatPhotoCache').then((m) => void m.deletePhotoDb());
  } catch {
    // ignore
  }
  try {
    void import('@/utils/chatMediaCache').then((m) => void m.purgeChatPhotoCache());
  } catch {
    // ignore
  }
  notifyAuthChanged();
}

export function setAuth(
  data: { access: string; refresh?: string | null; user?: unknown },
  rememberMe: boolean
) {
  if (typeof window === 'undefined') return;
  // Комментарий на русском: чтобы не подхватить "старый" токен из другого хранилища — чистим всё перед записью.
  clearAuth();
  const storage = rememberMe ? localStorage : sessionStorage;
  safeSet(storage, ACCESS_KEY, data.access);
  if (data.refresh) safeSet(storage, REFRESH_KEY, data.refresh);
  if (typeof data.user !== 'undefined') safeSet(storage, USER_KEY, JSON.stringify(data.user));

  // Комментарий на русском: cookie нужен для аналитики/маркеринга авторизации.
  document.cookie = rememberMe
    ? 'is_auth=1; Path=/; Max-Age=31536000; SameSite=Lax'
    : 'is_auth=1; Path=/; SameSite=Lax';
  notifyAuthChanged();
}


